import React, { Suspense,  useEffect } from 'react';
import './Toast.css';
import { useTranslation, Trans } from 'react-i18next';
import useBrowserLocale from '../../hooks/useBrowserLocale/useBrowserLocale';

export type NotificationType = 'success' | 'error' | 'warning';

export interface NotificationProps {
    /**
     * The main message to display
     */
    text: string;

    /**
     * Optional title for the notification
     */
    title?: string;

    /**
     * Type of notification that determines the color scheme
     * @default 'info'
     */
    type?: NotificationType;

    /**
     * Duration in milliseconds to show the notification
     * @default 5000
     */
    duration?: number;

    /**
     * Whether the notification is currently visible
     */
    isVisible: boolean;

    /**
     * Callback when the notification is closed
     */
    onClose: () => void;

    /**
     * Optional custom class names
     */
    className?: string;

    /**
     * Position of the notification
     * @default 'top-right'
     */
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
} 
const Toast: React.FC<NotificationProps> = ({
  text,
  type = 'error',
  duration = 5000,
  isVisible,
  onClose,
  title,
  position = 'top-right',
  className = '',
}) => {
  const locale = useBrowserLocale();
  const { t } = useTranslation('translation', { lng: locale });
  useEffect(() => {
    if (isVisible && duration) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const positionClass = `toast-${position}`;
  const typeClass = `toast-${type}`;

  return (
  <Suspense fallback={<div>Loading...</div>}>
    <div 
      className={`toast ${positionClass} ${typeClass} ${className}`}
      role="alert"
    >
      <div className="toast-content">
        {title && (
          <Trans i18nKey={title}>
            <p className="toast-title">{t(title)}</p>
          </Trans>
        )}
       {text && <Trans i18nKey={text}>
          <p className="toast-message">{t(text)}</p>
        </Trans>}
      </div>
      
      <button
        onClick={onClose}
        className="toast-close-button"
        aria-label="Close notification"
      >
        <svg 
          className="toast-close-icon"
          fill="none" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth="2" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  </Suspense>
  );
};

export default Toast;